import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabaseClient } from '../../config/supabase';
import Button3D from '../ui/Button3D';
import { D2_INPUT, D2_SECTION_TITLE } from '../settings/signatures/d2Fields';
import OutboundCallSettings from './OutboundCallSettings';
import TripLinePoolSettings from './TripLinePoolSettings';

function normalizePanelData(data) {
    return {
        numbers: Array.isArray(data?.numbers) ? data.numbers : [],
        settings: Array.isArray(data?.settings) ? data.settings : [],
        staff: Array.isArray(data?.staff) ? data.staff : [],
    };
}

export default function InboundCallSettingsPanel({ orgId, client = supabaseClient }) {
    const theme = typeof document !== 'undefined' && document.documentElement.classList.contains('dark') ? 'dark' : 'light';
    const [data, setData] = useState(null);
    const [numberId, setNumberId] = useState('');
    const [ringUserIds, setRingUserIds] = useState([]);
    const [ringSeconds, setRingSeconds] = useState(20);
    const [staffCallerIdMode, setStaffCallerIdMode] = useState('main_line');
    const [businessGreeting, setBusinessGreeting] = useState('');
    const [afterHoursGreeting, setAfterHoursGreeting] = useState('');
    const [missedCallMessage,setMissedCallMessage]=useState('Sorry we missed your call. Please leave a message after the tone.');
    const [rehearsalOnly, setRehearsalOnly] = useState(false);
    const [dryRun, setDryRun] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');

    const invoke = useCallback(async (body) => {
        const { data: result, error: invokeError } = await client.functions.invoke('twilio-inbound-voice', { body: { orgId, ...body } });
        if (invokeError) {
            let details = null;
            try { details = await invokeError.context?.json?.(); } catch { /* response body may already be consumed */ }
            const rollback = Array.isArray(details?.rollbackResults) ? details.rollbackResults.join('\n') : '';
            throw new Error([details?.error || invokeError.message, rollback, details?.auditError ? `Audit write failed: ${details.auditError}` : ''].filter(Boolean).join('\n'));
        }
        if (!result?.ok) throw new Error(result?.error || 'Phone-line request failed.');
        return result;
    }, [client, orgId]);

    const load = useCallback(async () => {
        setBusy(true);
        setError('');
        try {
            const result = await invoke({ action: 'load' });
            const next = normalizePanelData(result);
            setData(next);
            setNumberId((current) => next.numbers.some((row) => row.id === current) ? current : next.numbers[0]?.id || '');
        } catch (loadError) {
            setError(loadError?.message || 'Could not load phone-line settings.');
        } finally {
            setBusy(false);
        }
    }, [invoke]);

    useEffect(() => { load(); }, [load]);

    const selectedNumber = useMemo(() => data?.numbers.find((row) => row.id === numberId) || null, [data, numberId]);
    const selectedSettings = useMemo(() => data?.settings.find((row) => row.number_id === numberId) || null, [data, numberId]);
    const savedSettings = selectedSettings || {
        ring_user_ids: [],
        ring_seconds: 20,
        rehearsal_only: false,
        business_hours_greeting: 'Thank you for calling. Please hold while we connect you.',
        after_hours_greeting: 'We are closed. Please leave a message after the tone.',
    };
    const settingsDirty = JSON.stringify({
        ringUserIds,
        ringSeconds: Number(ringSeconds),
        staffCallerIdMode,
        rehearsalOnly,
        businessGreeting,
        afterHoursGreeting,
        missedCallMessage,
    }) !== JSON.stringify({
        ringUserIds: savedSettings.ring_user_ids || [],
        ringSeconds: savedSettings.ring_seconds || 20,
        staffCallerIdMode: savedSettings.staff_caller_id_mode || 'main_line',
        rehearsalOnly: savedSettings.rehearsal_only === true,
        businessGreeting: savedSettings.business_hours_greeting,
        afterHoursGreeting: savedSettings.after_hours_greeting,
        missedCallMessage:savedSettings.missed_call_message || 'Sorry we missed your call. Please leave a message after the tone.',
    });

    useEffect(() => {
        setRingUserIds(selectedSettings?.ring_user_ids || []);
        setRingSeconds(selectedSettings?.ring_seconds || 20);
        setStaffCallerIdMode(selectedSettings?.staff_caller_id_mode || 'main_line');
        setMissedCallMessage(selectedSettings?.missed_call_message || 'Sorry we missed your call. Please leave a message after the tone.');
        setBusinessGreeting(selectedSettings?.business_hours_greeting || 'Thank you for calling. Please hold while we connect you.');
        setAfterHoursGreeting(selectedSettings?.after_hours_greeting || 'We are closed. Please leave a message after the tone.');
        setRehearsalOnly(selectedSettings?.rehearsal_only === true);
        setDryRun(null);
    }, [selectedSettings, numberId]);

    useEffect(() => { setNotice(''); }, [numberId]);

    function updateRingUser(userId, checked) {
        setRingUserIds((current) => checked
            ? [...current, userId]
            : current.filter((id) => id !== userId));
        setDryRun(null);
    }

    async function saveSettings() {
        setBusy(true);
        setError('');
        setNotice('');
        try {
            await invoke({
                action: 'save-settings',
                numberId,
                ringUserIds,
                ringSeconds: Number(ringSeconds),
                staffCallerIdMode,
                businessHoursGreeting: businessGreeting,
                afterHoursGreeting,
                missedCallMessage,
                rehearsalOnly,
            });
            await load();
            setNotice('Call settings saved.');
        } catch (saveError) {
            setError(saveError?.message || 'Could not save call settings.');
        } finally {
            setBusy(false);
        }
    }

    async function runDryRun() {
        if (settingsDirty) {
            setNotice('Save call settings before running the dry-run. Dry-run itself makes no changes.');
            return;
        }
        setBusy(true);
        setError('');
        setNotice('');
        try {
            const result = await invoke({ action: 'dry-run', numberId, mode: rehearsalOnly ? 'rehearsal' : 'port-day' });
            setDryRun(result);
            setNotice(result.passed ? 'Dry-run passed. No phone-line or Twilio settings were changed.' : 'Dry-run found items that must be fixed before switching.');
        } catch (dryError) {
            setDryRun(null);
            setError(dryError?.message || 'Dry-run could not be completed.');
        } finally {
            setBusy(false);
        }
    }

    async function switchToTwilio() {
        if (!dryRun?.passed || !selectedNumber) return;
        const label = rehearsalOnly ? 'the rehearsal line' : 'this phone line';
        if (!window.confirm(`Switch ${label} to Twilio now? This updates the Twilio webhooks for this line.`)) return;
        setBusy(true);
        setError('');
        setNotice('');
        try {
            await invoke({ action: 'switch', numberId, mode: rehearsalOnly ? 'rehearsal' : 'port-day' });
            await load();
            setNotice(`Switched ${selectedNumber.phone_number} to Twilio. WhatsApp account settings were preserved.`);
            setDryRun(null);
        } catch (switchError) {
            setError(switchError?.message || 'Phone-line switch failed. Review the rollback results before trying again.');
        } finally {
            setBusy(false);
        }
    }

    if (!data && !error) return <div className="ia-d2 ia-d2-card rounded-xl p-4 text-sm text-[var(--ink-muted)]" aria-busy="true">Loading call settings…</div>;

    return (
        <section className="ia-d2 ia-d2-card space-y-4 rounded-xl p-4" data-theme={theme} aria-labelledby="inbound-calls-title" data-testid="inbound-call-settings">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 id="inbound-calls-title" className={D2_SECTION_TITLE}>Calls and phone line</h3>
                {data?.numbers?.length > 0 && (
                    <label className="flex w-full min-w-0 flex-col items-start gap-2 text-sm font-bold sm:w-auto sm:flex-row sm:items-center">
                        Phone line
                        <select
                            aria-label="Phone line"
                            className={`${D2_INPUT} w-full min-w-0 max-w-full sm:w-auto`}
                            value={numberId}
                            disabled={busy}
                            onChange={(event) => { setNumberId(event.target.value); setDryRun(null); }}
                        >
                            {data.numbers.map((row) => <option key={row.id} value={row.id}>{row.label || row.phone_number} · {row.phone_number}</option>)}
                        </select>
                    </label>
                )}
            </div>
            {error && <p role="alert" className="whitespace-pre-wrap rounded-lg border border-red-500/50 bg-red-500/10 p-3 text-sm">{error}</p>}
            {!selectedNumber && <p className="text-sm text-[var(--ink-muted)]">No Twilio phone lines are configured for this organization.</p>}
            {selectedNumber && (
                <>
                    <div className="space-y-3">
                        <fieldset className="space-y-2">
                            <legend className="text-sm font-bold">Staff who ring at the same time</legend>
                            <p className="text-xs text-[var(--ink-muted)]">Each selected person’s saved staff mobile rings simultaneously. Up to 10 people. The whisper identifies the company and caller; staff press 1 to accept.</p>
                            <div className="grid gap-2 sm:grid-cols-2">
                                {(data?.staff || []).map((staff) => (
                                    <label key={staff.userId} className="flex items-center gap-2 rounded-lg border border-[var(--border)] px-3 py-2 text-sm">
                                        <input
                                            type="checkbox"
                                            checked={ringUserIds.includes(staff.userId)}
                                            onChange={(event) => updateRingUser(staff.userId, event.target.checked)}
                                        />
                                        <span>{staff.name} <span className="text-xs text-[var(--ink-muted)]">{staff.mobile}</span></span>
                                    </label>
                                ))}
                                {data?.staff?.length === 0 && <p className="text-sm text-amber-700 dark:text-amber-300">No active staff members with a saved mobile are available.</p>}
                            </div>
                        </fieldset>
                        <div className="grid gap-3 sm:grid-cols-2">
                            <label className="flex flex-col gap-1 text-sm font-bold">
                                Staff phones show
                                <select aria-label="Staff phones show" className={D2_INPUT} value={staffCallerIdMode} onChange={(event) => { setStaffCallerIdMode(event.target.value); setDryRun(null); }}>
                                    <option value="main_line">Main line</option>
                                    <option value="caller_number">Caller&apos;s number</option>
                                </select>
                                <span className="text-xs font-normal text-[var(--ink-muted)]">Main line uses this business number. Anonymous callers always show the main line.</span>
                            </label>
                            <label className="flex flex-col gap-1 text-sm font-bold">
                                Ring time
                                <select aria-label="Ring time" className={D2_INPUT} value={ringSeconds} onChange={(event) => { setRingSeconds(Number(event.target.value)); setDryRun(null); }}>
                                    {[10, 15, 20, 25, 30, 45, 60].map((seconds) => <option key={seconds} value={seconds}>{seconds} seconds</option>)}
                                </select>
                            </label>
                            <label className="flex items-center gap-2 self-end rounded-lg border border-[var(--border)] px-3 py-2 text-sm">
                                <input type="checkbox" checked={rehearsalOnly} onChange={(event) => { setRehearsalOnly(event.target.checked); setDryRun(null); }} />
                                Rehearsal-only line (blocks port-day mode)
                            </label>
                        </div>
                        <label className="flex flex-col gap-1 text-sm font-bold">
                            Business-hours greeting
                            <textarea className={D2_INPUT} rows={2} maxLength={500} value={businessGreeting} onChange={(event) => { setBusinessGreeting(event.target.value); setDryRun(null); }} />
                        </label>
                        <label className="flex flex-col gap-1 text-sm font-bold">
                            After-hours greeting
                            <textarea className={D2_INPUT} rows={2} maxLength={500} value={afterHoursGreeting} onChange={(event) => { setAfterHoursGreeting(event.target.value); setDryRun(null); }} />
                        </label>
                        <label className="block text-sm font-semibold">Missed call message
                            <textarea className={D2_INPUT} rows={2} maxLength={500} value={missedCallMessage} onChange={(event)=>{setMissedCallMessage(event.target.value);setDryRun(null);}} />
                            <span className="block text-xs">Played after ringing when nobody answers. The business greeting is not repeated.</span>
                        </label>
                    </div>
                    <div className="flex flex-wrap gap-2 border-t border-[var(--border)] pt-3">
                        <Button3D variant="light" onClick={saveSettings} disabled={busy}>Save call settings</Button3D>
                        <Button3D variant="light" onClick={runDryRun} disabled={busy || settingsDirty}>
                            {rehearsalOnly ? 'Dry-run rehearsal line' : 'Dry-run port-day switch'}
                        </Button3D>
                        <Button3D variant="go" onClick={switchToTwilio} disabled={busy || settingsDirty || !dryRun?.passed}>
                            Switch to Twilio
                        </Button3D>
                    </div>
                    {settingsDirty && <p className="text-xs text-amber-700 dark:text-amber-300">Save call settings before running a dry-run.</p>}
                    {notice && <p role="status" className="text-sm text-emerald-700 dark:text-emerald-300">{notice}</p>}
                    {dryRun && (
                        <div className="space-y-2 rounded-lg border border-[var(--border)] p-3" data-testid="phone-line-dry-run">
                            <h4 className="text-sm font-bold">{dryRun.passed ? 'Dry-run passed' : 'Dry-run needs attention'}</h4>
                            {dryRun.serviceSid && (
                                <p className="text-xs" data-testid="phone-line-service">
                                    Messaging Service: {dryRun.serviceSid}{dryRun.campaignSid ? ` · A2P campaign ${dryRun.campaignSid}` : ''}
                                </p>
                            )}
                            <ul className="space-y-1 text-sm">
                                {(dryRun.checks || []).map((check) => (
                                    <li key={check.key} className={check.passed ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'}>
                                        {check.passed ? 'PASS' : 'FAIL'} — {check.message}
                                    </li>
                                ))}
                            </ul>
                            <p className="text-xs text-[var(--ink-muted)]">
                                WhatsApp keeps its current account and sender setting. A carrier port does not prove that the WhatsApp sender remains registered; verify sender status with Twilio/Meta on port day.
                            </p>
                        </div>
                    )}
                    <p className="text-xs text-[var(--ink-muted)]">
                        Missed calls and voicemail recordings appear in Communications. The selected line must have configured staff ring targets and verified Twilio messaging.
                    </p>
                </>
            )}
            <OutboundCallSettings orgId={orgId} client={client} />
            <TripLinePoolSettings orgId={orgId} client={client} />
        </section>
    );
}
